import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api, type ReceiptHistoryItem } from "../lib/api";
import { AppError, logError, toUserMessage } from "../lib/errors";
import { getReceiptStatusBadge } from "../components/ReceiptStatusBadge";
import { ReceiptMonthlySummaryCard } from "../components/ReceiptMonthlySummaryCard";
import { ReceiptSettlementReminderPreferenceCard } from "../components/ReceiptSettlementReminderPreferenceCard";

function formatReceiptDate(value: string | null) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", { month: "numeric", day: "numeric" }).format(d);
}

function formatProcessedAt(value: string | null) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function formatAmount(amount: string | null, currency: string | null) {
  if (!amount) return null;
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  const sym = currency === "JPY" || !currency ? "¥" : currency;
  return `${sym}${n.toLocaleString("ja-JP")}`;
}

function ReceiptCard({
  item,
  selected,
  onToggleSelect,
}: {
  item: ReceiptHistoryItem;
  selected: boolean;
  onToggleSelect: (jobId: string) => void;
}) {
  const badge = getReceiptStatusBadge(item);
  const amount = formatAmount(item.totalAmount, item.currency);
  const dateDisplay = formatReceiptDate(item.receiptDate) ?? formatProcessedAt(item.processedAt);
  const selectable = item.relayStatus === "completed" && !item.needsReview;

  return (
    <article className="overflow-hidden rounded-2xl border border-[#EADBC8] bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          {selectable && (
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggleSelect(item.jobId)}
              aria-label="精算対象として選択"
              className="mt-1 h-4 w-4 shrink-0 accent-[#A67C52]"
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-black ${badge.className}`}>
                {badge.label}
              </span>
              {dateDisplay && (
                <span className="text-xs text-[#9C867D]">{dateDisplay}</span>
              )}
            </div>
            <p className="mt-1 truncate text-sm font-black text-[#5D4D46]">
              {item.merchantName ?? "（店舗名不明）"}
            </p>
            {item.relayStatus === "accepted" && (
              <p className="mt-0.5 text-xs text-[#9C867D]">OCR処理中...</p>
            )}
            {item.relayStatus === "failed" && (
              <p className="mt-0.5 text-xs text-[#B4533A]">処理に失敗しました</p>
            )}
            {amount && (
              <p className="mt-1 text-base font-black text-[#5D4D46]">{amount}</p>
            )}
          </div>
        </div>
        <Link
          to={`/receipt/${item.jobId}`}
          className="min-h-[42px] w-full shrink-0 rounded-full bg-[#7C5A4E] px-4 py-2 text-center text-sm font-black text-white shadow-sm transition hover:bg-[#5D4D46] sm:w-auto"
        >
          詳細を見る
        </Link>
      </div>
    </article>
  );
}

type FilterValues = {
  dateFrom: string;
  dateTo: string;
  amountMin: string;
  amountMax: string;
  needsReview: "" | "true" | "false";
};

const EMPTY_FILTER: FilterValues = { dateFrom: "", dateTo: "", amountMin: "", amountMax: "", needsReview: "" };

function hasFilter(f: FilterValues) {
  return f.dateFrom !== "" || f.dateTo !== "" || f.amountMin !== "" || f.amountMax !== "" || f.needsReview !== "";
}

function FilterPanel({
  onApply,
  onReset,
  loading,
}: {
  onApply: (f: FilterValues) => void;
  onReset: () => void;
  loading: boolean;
}) {
  const [draft, setDraft] = useState<FilterValues>(EMPTY_FILTER);
  const applied = useRef<FilterValues>(EMPTY_FILTER);

  const set = (key: keyof FilterValues) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((prev) => ({ ...prev, [key]: e.target.value }));

  const setNeedsReview = (value: FilterValues["needsReview"]) =>
    setDraft((prev) => ({ ...prev, needsReview: value }));

  const handleApply = () => {
    applied.current = draft;
    onApply(draft);
  };

  const handleReset = () => {
    setDraft(EMPTY_FILTER);
    applied.current = EMPTY_FILTER;
    onReset();
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(applied.current);

  return (
    <div className="mt-4 rounded-2xl border border-[#E8D5C8] bg-[#FFFAF7] p-4">
      <p className="mb-3 text-xs font-black tracking-wide text-[#A67C52]">絞り込み</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">領収書日付（開始）</label>
          <input
            type="date"
            value={draft.dateFrom}
            onChange={set("dateFrom")}
            className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">領収書日付（終了）</label>
          <input
            type="date"
            value={draft.dateTo}
            onChange={set("dateTo")}
            className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">金額（最小）</label>
          <input
            type="number"
            min="0"
            value={draft.amountMin}
            onChange={set("amountMin")}
            placeholder="例: 1000"
            className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">金額（最大）</label>
          <input
            type="number"
            min="0"
            value={draft.amountMax}
            onChange={set("amountMax")}
            placeholder="例: 50000"
            className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
          />
        </div>
      </div>
      <div className="mt-3">
        <p className="mb-2 text-xs font-bold text-[#7C5A4E]">ステータス</p>
        <div className="flex flex-wrap gap-4">
          {([["", "すべて"], ["true", "要確認"], ["false", "確認済"]] as const).map(([val, label]) => (
            <label key={val} className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-bold text-[#5D4D46]">
              <input
                type="radio"
                name="needsReview"
                value={val}
                checked={draft.needsReview === val}
                onChange={() => setNeedsReview(val)}
                className="accent-[#A67C52]"
              />
              {label}
            </label>
          ))}
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={handleApply}
          disabled={loading || !dirty}
          className="flex-1 rounded-full bg-[#A67C52] py-2 text-sm font-black text-white hover:bg-[#7C5A4E] disabled:opacity-50"
        >
          絞り込む
        </button>
        <button
          type="button"
          onClick={handleReset}
          disabled={loading}
          className="rounded-full border border-[#A67C52] px-4 py-2 text-sm font-black text-[#A67C52] hover:bg-[#F5EDE8] disabled:opacity-50"
        >
          リセット
        </button>
      </div>
    </div>
  );
}

export default function ReceiptHistoryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ReceiptHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterValues>(EMPTY_FILTER);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [settling, setSettling] = useState(false);
  const [settleError, setSettleError] = useState<string | null>(null);
  const [settleNotice, setSettleNotice] = useState<string | null>(null);
  const [selectingAll, setSelectingAll] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const historyListRef = useRef<HTMLElement>(null);

  const loadPage = useCallback(
    async (page: number, append: boolean, filter: FilterValues) => {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      setErrorMessage(null);

      try {
        const params: Parameters<typeof api.getReceiptHistory>[0] = { page, perPage: 20 };
        if (filter.dateFrom) params.dateFrom = filter.dateFrom;
        if (filter.dateTo) params.dateTo = filter.dateTo;
        if (filter.amountMin !== "") params.amountMin = Number(filter.amountMin);
        if (filter.amountMax !== "") params.amountMax = Number(filter.amountMax);
        if (filter.needsReview !== "") params.needsReview = filter.needsReview === "true";

        const list = await api.getReceiptHistory(params);
        setItems((prev) => (append ? [...prev, ...list.data] : list.data));
        setCurrentPage(list.meta.currentPage);
        setLastPage(list.meta.lastPage);
        setTotal(list.meta.total);
        return list.meta.total > 0;
      } catch (error) {
        if (error instanceof AppError && error.code === "SESSION_EXPIRED") {
          navigate("/splash", { replace: true });
          return;
        }
        logError(error);
        setErrorMessage(toUserMessage(error));
        return false;
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    void loadPage(1, false, EMPTY_FILTER);
  }, [loadPage]);

  const handleApplyFilter = async (f: FilterValues) => {
    setActiveFilter(f);
    setSettleNotice(null);
    const hasResults = await loadPage(1, false, f);
    if (hasResults) {
      historyListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleResetFilter = () => {
    setActiveFilter(EMPTY_FILTER);
    setSettleNotice(null);
    void loadPage(1, false, EMPTY_FILTER);
  };

  const toggleSelect = (jobId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const handleSelectAllFiltered = async () => {
    setSelectingAll(true);
    setSettleError(null);
    try {
      // 絞り込み結果の全件(表示中のページに限らない)を対象にIDを集める。
      // 今月分ショートカットと同様、辻褄の合わないページング応答での無限ループを防ぐため上限を設ける。
      const MAX_SELECT_PAGES = 200;
      const jobIds: string[] = [];
      let page = 1;
      let pageLimitExceeded = false;
      for (; page <= MAX_SELECT_PAGES; page++) {
        const params: Parameters<typeof api.getReceiptHistory>[0] = { page, perPage: 50 };
        if (activeFilter.dateFrom) params.dateFrom = activeFilter.dateFrom;
        if (activeFilter.dateTo) params.dateTo = activeFilter.dateTo;
        if (activeFilter.amountMin !== "") params.amountMin = Number(activeFilter.amountMin);
        if (activeFilter.amountMax !== "") params.amountMax = Number(activeFilter.amountMax);
        if (activeFilter.needsReview !== "") params.needsReview = activeFilter.needsReview === "true";

        const list = await api.getReceiptHistory(params);
        jobIds.push(
          ...list.data
            .filter((item) => item.relayStatus === "completed" && !item.needsReview)
            .map((item) => item.jobId)
        );
        if (page >= list.meta.lastPage) break;

        if (page === MAX_SELECT_PAGES) {
          pageLimitExceeded = true;
          break;
        }
      }

      if (pageLimitExceeded) {
        setSettleError(
          "対象件数が多すぎるため、すべて選択できませんでした。絞り込みを行ってから操作してください。"
        );
        return;
      }

      setSelectedIds(new Set(jobIds));
    } catch (error) {
      if (error instanceof AppError && error.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }
      logError(error);
      setSettleError(toUserMessage(error));
    } finally {
      setSelectingAll(false);
    }
  };

  const handleSettleSelected = async (isSettled: boolean) => {
    const jobIds = Array.from(selectedIds);
    if (jobIds.length === 0) return;
    setSettling(true);
    setSettleError(null);
    setSettleNotice(null);
    try {
      await api.settleReceiptJobs({ jobIds, isSettled });
      setSelectedIds(new Set());
      await loadPage(1, false, activeFilter);
    } catch (error) {
      if (error instanceof AppError && error.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }
      logError(error);
      setSettleError(toUserMessage(error));
    } finally {
      setSettling(false);
    }
  };

  const handleSettleCurrentMonth = async () => {
    setSettling(true);
    setSettleError(null);
    setSettleNotice(null);
    try {
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      const year = now.getFullYear();
      const month = now.getMonth();
      const dateFrom = `${year}-${pad(month + 1)}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const dateTo = `${year}-${pad(month + 1)}-${pad(lastDay)}`;

      // 未精算のみを対象に、一覧APIのページング(最大50件)を走査してIDを集める。
      // list.meta.lastPage の値がどのようなものであっても無限ループにならないよう、
      // 走査するページ数に上限を設ける(1ページ50件 × 200ページ = 10,000件、現実的な
      // 月内件数を大きく超える余裕を持たせた上限)。
      const MAX_SETTLE_PAGES = 200;
      const jobIds: string[] = [];
      let page = 1;
      let pageLimitExceeded = false;
      for (; page <= MAX_SETTLE_PAGES; page++) {
        const list = await api.getReceiptHistory({
          page,
          perPage: 50,
          dateFrom,
          dateTo,
          isSettled: false,
        });
        jobIds.push(...list.data.map((item) => item.jobId));
        if (page >= list.meta.lastPage) break;

        if (page === MAX_SETTLE_PAGES) {
          pageLimitExceeded = true;
          break;
        }
      }

      if (pageLimitExceeded) {
        setSettleError(
          "対象件数が多すぎるため、今月分をまとめて精算済みにできませんでした。絞り込みを行ってから個別に操作してください。"
        );
        return;
      }

      if (jobIds.length === 0) {
        setSettleNotice("今月の未精算の領収書はありませんでした。");
        return;
      }

      // job_ids の上限(100件)を超える場合はチャンク分割して呼び出す
      const chunkSize = 100;
      for (let i = 0; i < jobIds.length; i += chunkSize) {
        const chunk = jobIds.slice(i, i + chunkSize);
        await api.settleReceiptJobs({ jobIds: chunk, isSettled: true });
      }

      setSelectedIds(new Set());
      await loadPage(1, false, activeFilter);
    } catch (error) {
      if (error instanceof AppError && error.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }
      logError(error);
      setSettleError(toUserMessage(error));
    } finally {
      setSettling(false);
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const params: Parameters<typeof api.exportReceiptsCsv>[0] = {};
      if (activeFilter.dateFrom) params.dateFrom = activeFilter.dateFrom;
      if (activeFilter.dateTo) params.dateTo = activeFilter.dateTo;
      if (activeFilter.amountMin !== "") params.amountMin = Number(activeFilter.amountMin);
      if (activeFilter.amountMax !== "") params.amountMax = Number(activeFilter.amountMax);
      if (activeFilter.needsReview !== "") params.needsReview = activeFilter.needsReview === "true";

      const { blob, filename } = await api.exportReceiptsCsv(params);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      if (error instanceof AppError && error.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }
      logError(error);
      setExportError(toUserMessage(error));
    } finally {
      setExporting(false);
    }
  };

  const hasMore = currentPage < lastPage;
  const isFiltered = hasFilter(activeFilter);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/receipt" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ←戻る
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">領収書の記録</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <ReceiptMonthlySummaryCard />

        <section className="rounded-[2rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-black tracking-[0.12em] text-[#A67C52]">領収書整理</p>
              <h1 className="mt-3 text-2xl font-black tracking-tight text-[#7C5A4E]">領収書の記録</h1>
            </div>
            <Link
              to="/receipt"
              className="inline-flex min-h-[40px] items-center justify-center rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#5D4D46]"
            >
              新しい領収書を追加
            </Link>
          </div>

          <FilterPanel
            onApply={handleApplyFilter}
            onReset={handleResetFilter}
            loading={loading || loadingMore}
          />

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void handleSettleCurrentMonth()}
              disabled={settling}
              className="rounded-full border border-[#A67C52] px-4 py-2 text-sm font-black text-[#A67C52] transition hover:bg-[#F5EDE8] disabled:opacity-50"
            >
              今月分をまとめて精算済みにする
            </button>
            <button
              type="button"
              onClick={() => void handleExportCsv()}
              disabled={exporting}
              className="rounded-full border border-[#A67C52] px-4 py-2 text-sm font-black text-[#A67C52] transition hover:bg-[#F5EDE8] disabled:opacity-50"
            >
              {exporting ? "出力中..." : "CSVで出力"}
            </button>
            <ReceiptSettlementReminderPreferenceCard />
          </div>

          {!loading && errorMessage && (
            <p className="mt-4 rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">{errorMessage}</p>
          )}
          {settleError && (
            <p className="mt-4 rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">{settleError}</p>
          )}
          {settleNotice && (
            <p className="mt-4 rounded-2xl bg-[#FFF7F2] p-3 text-sm font-bold text-[#7C5A4E]">{settleNotice}</p>
          )}
          {exportError && (
            <p className="mt-4 rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">{exportError}</p>
          )}
        </section>

        {selectedIds.size > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl border border-[#A67C52]/40 bg-[#FFF7F2] p-3">
            <p className="text-sm font-bold text-[#7C5A4E]">{selectedIds.size}件選択中</p>
            <button
              type="button"
              onClick={() => void handleSettleSelected(true)}
              disabled={settling}
              className="rounded-full bg-[#A67C52] px-4 py-2 text-sm font-black text-white transition hover:bg-[#7C5A4E] disabled:opacity-50"
            >
              精算済みにする
            </button>
            <button
              type="button"
              onClick={() => void handleSettleSelected(false)}
              disabled={settling}
              className="rounded-full border border-[#A67C52] px-4 py-2 text-sm font-black text-[#A67C52] transition hover:bg-[#F5EDE8] disabled:opacity-50"
            >
              精算を解除
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]"
            >
              選択解除
            </button>
          </div>
        )}

        <section
          ref={historyListRef}
          aria-label="領収書の記録一覧"
          className="mt-5 rounded-[2rem] border border-[#EADBC8] bg-white/80 p-4 shadow-[0_18px_48px_rgba(124,90,78,0.12)] sm:p-5"
        >
          {!loading && total !== null && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-[#9C867D]">
                {isFiltered ? `絞り込み結果：${total}件` : `全${total}件`}
              </p>
              {total > 0 && (
                <button
                  type="button"
                  onClick={() => void handleSelectAllFiltered()}
                  disabled={selectingAll || settling}
                  className="text-xs font-bold text-[#A67C52] hover:text-[#7C5A4E] disabled:opacity-50"
                >
                  {selectingAll ? "選択中..." : "すべて選択"}
                </button>
              )}
            </div>
          )}

          {loading ? (
            <p className="py-10 text-center text-sm text-[#9C867D]">読み込み中...</p>
          ) : !errorMessage && items.length === 0 ? (
            <div className="flex min-h-[240px] items-center justify-center rounded-2xl border border-dashed border-[#EADBC8] bg-[#FFFCFA] p-5 text-center text-sm leading-7 text-[#6B5851]">
              {isFiltered ? "条件に一致する領収書がありません。" : "領収書の記録がありません。"}
            </div>
          ) : (
            <div className="grid gap-3">
              {items.map((item) => (
                <ReceiptCard
                  key={item.jobId}
                  item={item}
                  selected={selectedIds.has(item.jobId)}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </div>
          )}

          {hasMore && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => void loadPage(currentPage + 1, true, activeFilter)}
                disabled={loadingMore}
                className="rounded-full border border-[#D8C0AF] bg-white px-6 py-2.5 text-sm font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2] disabled:opacity-50"
              >
                {loadingMore ? "読み込み中..." : "もっと見る"}
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
